import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import UserList from "./UserList";
import OrgSettingsModal from "../components/OrgSettingsModal";
import { useHttpClient } from "../../shared/hooks/http-hook";
import { ArrowLeft, Settings } from "lucide-react";
import { Card, Spinner, CardGroup } from "react-bootstrap";
import dateFormat, { masks } from "dateformat";

const OrgSettings = () => {
	//PAGE IS FOR ADMINS ONLY
	const { orgId } = useParams();
	const { sendRequest } = useHttpClient();
	const navigate = useNavigate();
	masks.displayDate = "mmm d";

	const [loadedOrgData, setLoadedOrgData] = useState(null);
	const [loadedMetrics, setLoadedMetrics] = useState(null);

	const [showModal, setShowModal] = useState(false);

	function handleOrgUpdated() {
		fetchOrgData();
		fetchOrgMetrics();
	}

	const fetchOrgData = useCallback(async () => {
		try {
			const responseData = await sendRequest(`/api/organizations/${orgId}`);
			setLoadedOrgData(responseData.data);
		} catch (err) {
			console.error("Error fetching organization data:", err);
		}
	}, [sendRequest, orgId]);

	const fetchOrgMetrics = useCallback(async () => {
		try {
			const responseData = await sendRequest(
				`/api/organization/metrics/${orgId}`
			);
			setLoadedMetrics(responseData.data);
		} catch (err) {
			console.error("Error fetching metrics:", err);
		}
	}, [sendRequest, orgId]);

	useEffect(() => {
		fetchOrgData();
		fetchOrgMetrics();
	}, [fetchOrgData, fetchOrgMetrics]);

	return (
		<div>
			<div className="min-h-screen bg-white p-4">
				<div className="flex items-center justify-between space-x-4 mb-4">
					<button
						className="p-2 hover:bg-gray-100 rounded-full transition-colors"
						onClick={() => navigate("/admin")}
					>
						<ArrowLeft className="w-6 h-6" />
					</button>
					<h2 className="text-xl font-semibold">{loadedOrgData?.name}</h2>
					{/* Settings button*/}
					<button
						className="p-2 hover:bg-gray-100 rounded-full transition-colors"
						onClick={() => setShowModal(true)}
					>
						<Settings className="w-6 h-6" />
					</button>
				</div>

				{!loadedOrgData ? (
					<div className="flex items-center justify-center mt-20">
						<Spinner animation="border" />
					</div>
				) : (
					<Card className="p-4">
						<Card.Body>
							<CardGroup className="mb-3">
								<Card>
									<Card.Body>
										<Card.Title>Credits Available</Card.Title>
										<Card.Text>{loadedOrgData.remainingCredits}</Card.Text>
									</Card.Body>
								</Card>
								<Card>
									<Card.Body>
										<Card.Title>Last Credit Reset</Card.Title>
										<Card.Text>
											{loadedOrgData.lastCreditReset
												? dateFormat(
														new Date(
															loadedOrgData.lastCreditReset._seconds * 1000
														),
														"displayDate"
												  )
												: ""}
										</Card.Text>
									</Card.Body>
								</Card>
								<Card>
									<Card.Body>
										<Card.Title>Tier</Card.Title>
										<Card.Text>
											{loadedOrgData.tier
												? loadedOrgData.tier.charAt(0).toUpperCase() +
												  loadedOrgData.tier.slice(1)
												: ""}
										</Card.Text>
									</Card.Body>
								</Card>
							</CardGroup>
							{!loadedMetrics ? (
								<p>Loading metrics...</p>
							) : (
								<CardGroup className="mb-3">
									<Card>
										<Card.Body>
											<Card.Title>Total Avatars</Card.Title>
											<Card.Text>{loadedMetrics.avatars}</Card.Text>
										</Card.Body>
									</Card>
									<Card>
										<Card.Body>
											<Card.Title>Total Scripts</Card.Title>
											<Card.Text>{loadedMetrics.scripts}</Card.Text>
										</Card.Body>
									</Card>
									<Card>
										<Card.Body>
											<Card.Title>Total Videos</Card.Title>
											<Card.Text>{loadedMetrics.videos}</Card.Text>
										</Card.Body>
									</Card>
								</CardGroup>
							)}
							<UserList orgId={orgId} />
						</Card.Body>
					</Card>
				)}
				<OrgSettingsModal
					show={showModal}
					onHide={() => setShowModal(false)}
					loadedOrgData={loadedOrgData}
					onOrgUpdated={handleOrgUpdated}
				/>
			</div>
		</div>
	);
};

export default OrgSettings;
